import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  User,
  UserRole,
  UserWithPermissions,
  AuthState,
  AuthActions,
  LoginCredentials,
  EntrepreneurState,
  AdminState,
  StaffUser,
  EntrepreneurUser,
  isStaffRole,
} from '@/types/auth';
import { ePath, tPath, loginHref, isEntrepreneurPath, hostPlatform, PLATFORMS, resolveSameOriginRedirect } from '@/utils/paths';
import { loginErrorMessage } from '@/lib/errors';
import { carrying, isAccountInactive, withParam } from '@/lib/accountActivation';
import type { StaffPlatform } from '@/types/org';

interface AuthStore extends AuthState, AuthActions {}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      permissions: [],
      org_features: [],
      platforms: [],
      isAuthenticated: false,
      isLoading: false,
      isAuthVerified: false,
      error: null,
      currentRole: null,

      // Core authentication methods
      loginWithCredentials: async (credentials: LoginCredentials, endpoint: string): Promise<User> => {
        const { setLoading, setError, setUser } = get();
        
        try {
          setLoading(true);
          setError(null);
          console.log('[AuthStore] loginWithCredentials called', { endpoint, email: credentials.email });

          // Call the password login endpoint
          const { callApi } = await import('@/hooks/useFetch');
          const userData: UserWithPermissions = await callApi(endpoint, {
            method: 'POST',
            data: credentials
          });
          
          console.log('[AuthStore] Login response received', { userId: userData.user.id, role: userData.user.role });
          setUser(userData);
          return userData.user;

        } catch (error: any) {
          console.error('[AuthStore] Login failed:', error);
          console.error('[AuthStore] Stack trace:', error.stack);
          console.error('[AuthStore] Error response:', error.response?.data);
          setError(loginErrorMessage(error));
          throw error;
        } finally {
          setLoading(false);
        }
      },


      logout: async (role?: UserRole): Promise<void> => {
        const { user, clearCredentials } = get();
        const userRole = role || user?.role;

        // Experts are staff: they hold the staff `vess_org_access_token`
        // cookie and log out through the staff portal like every other staff
        // role. There is no expert auth portal, so the old expert branch
        // 404'd and left the cookie alive.
        const logoutEndpoint = userRole === UserRole.ENTREPRENEUR
          ? '/entrepreneur/auth/logout'
          : '/org/auth/logout';

        try {
          const { callApi } = await import('@/hooks/useFetch');
          await callApi(logoutEndpoint, { method: 'POST' });
        } catch {
          // Continue with client-side cleanup even if backend call fails
        }

        clearCredentials();

        if (userRole === UserRole.ENTREPRENEUR) {
          window.location.href = ePath('/login');
        } else {
          window.location.href = loginHref();
        }
      },

      refreshAuth: async (): Promise<void> => {
        const { setLoading, setError, setUser, clearCredentials } = get();

        try {
          setLoading(true);
          setError(null);

          let userData: UserWithPermissions | null = null;

          const path = window.location.pathname;
          let meEndpoint: string;

          if (isEntrepreneurPath(path) || path.includes('/entrepreneur/auth/success')) {
            meEndpoint = '/entrepreneur/me';
          } else {
            meEndpoint = '/org/auth/me';
          }

          console.log('[AuthStore] refreshAuth called', { path, meEndpoint });

          const { callApi } = await import('@/hooks/useFetch');

          try {
            userData = await callApi(meEndpoint);
            console.log('[AuthStore] User data fetched successfully', {
              userId: userData?.user?.id,
              role: userData?.user?.role,
              state: (userData?.user as any)?.state,
              entrepreneurState: (userData?.user as any)?.entrepreneur_state
            });
          } catch (error: any) {
            const status = error?.response?.status;

            if (status === 401 || status === 403) {
              userData = await fetchOtherSession(callApi, meEndpoint);
              if (!userData) {
                console.log('[AuthStore] Server rejected credentials, clearing session', { status, meEndpoint });
                clearCredentials();
                return;
              }
              console.log('[AuthStore] Session of the other kind found', { meEndpoint, role: userData.user.role });
            } else if (meEndpoint === '/entrepreneur/me') {
              console.log('[AuthStore] Entrepreneur endpoint failed, trying auth endpoint');
              try {
                userData = await callApi('/auth/entrepreneur/me');
                console.log('[AuthStore] Auth endpoint succeeded', {
                  userId: userData?.user?.id,
                  role: userData?.user?.role
                });

                if (userData?.user?.role === 'ENTREPRENEUR') {
                  try {
                    const entrepreneurData: any = await callApi('/entrepreneur/me');
                    if (entrepreneurData?.user) {
                      userData = {
                        ...userData,
                        user: {
                          ...userData.user,
                          ...entrepreneurData.user,
                          entrepreneur_state: entrepreneurData.user.entrepreneur_state || entrepreneurData.user.status
                        }
                      };
                    }
                  } catch (enrichError: any) {
                    if (enrichError?.response?.status === 401 || enrichError?.response?.status === 403) {
                      console.log('[AuthStore] Server rejected on enrich, clearing session');
                      clearCredentials();
                      return;
                    }
                    console.log('[AuthStore] Could not enrich with entrepreneur details');
                  }
                }
              } catch (authError: any) {
                const fallbackStatus = authError?.response?.status;
                if (fallbackStatus === 401 || fallbackStatus === 403) {
                  console.log('[AuthStore] All endpoints rejected credentials, clearing session');
                  clearCredentials();
                  return;
                }
                console.error('[AuthStore] Both endpoints failed:', authError);
              }
            } else {
              console.error('[AuthStore] Failed to fetch user data:', error);
            }
          }

          if (userData) {
            setUser(userData);
          } else {
            console.log('[AuthStore] No valid authentication found, clearing session');
            clearCredentials();
          }

        } catch (error: any) {
          console.error('[AuthStore] refreshAuth error:', error);
          setError(error.message || 'Failed to refresh authentication');
          clearCredentials();
        } finally {
          set({ isAuthVerified: true });
          setLoading(false);
        }
      },

      // State management
      setUser: (userData: UserWithPermissions | null) => {
        if (userData) {
          set({
            user: userData.user,
            permissions: userData.permissions,
            org_features: userData.org_features ?? [],
            platforms: userData.platforms ?? [],
            isAuthenticated: true,
            currentRole: userData.user.role,
            error: null
          });
        } else {
          get().clearCredentials();
        }
      },

      clearCredentials: () => {
        set({
          user: null,
          permissions: [],
          org_features: [],
          platforms: [],
          isAuthenticated: false,
          isAuthVerified: false,
          currentRole: null,
          error: null
        });
      },

      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),

      // Navigation and redirects
      handleAuthRedirect: (currentPath: string, error?: any) => {
        const { isAuthenticated, user, redirectToLogin, redirectToDashboard } = get();

        // Handle 401 errors
        if (error?.response?.status === 401 || error?.status === 401) {
          get().clearCredentials();

          const role = isEntrepreneurPath(currentPath) ? UserRole.ENTREPRENEUR : UserRole.SUPERADMIN;

          if (!currentPath.includes('/login')) {
            redirectToLogin(role, currentPath);
          }
          return;
        }

        // Handle successful authentication on login pages
        if (user && isAuthenticated && currentPath.includes('/login')) {
          redirectToDashboard(user.role as UserRole);
          return;
        }

        // Handle protected routes without credentials
        if (!isAuthenticated && isProtectedRoute(currentPath)) {
          const role = isEntrepreneurPath(currentPath) ? UserRole.ENTREPRENEUR : UserRole.SUPERADMIN;
          redirectToLogin(role, currentPath);
          return;
        }
      },

      redirectToLogin: (role: UserRole, redirectPath?: string, loginUrl?: string) => {
        let url = loginUrl ?? (role === UserRole.ENTREPRENEUR ? ePath('/login') : loginHref());

        if (redirectPath && redirectPath !== '/' && !redirectPath.includes('/login')) {
          url += `?redirect=${encodeURIComponent(redirectPath)}`;
        }

        window.location.href = url;
      },

      /** Close a session whose account is not active and send the person to
       * the login of that session's portal, carrying where they were going and
       * why they were sent back (`error`, a login refusal code). */
      endInactiveSession: async (error: string): Promise<void> => {
        const { user, clearCredentials } = get();
        if (!user) return;
        const isFounder = user.role === UserRole.ENTREPRENEUR;
        const { callApi } = await import('@/hooks/useFetch');
        await callApi(isFounder ? '/entrepreneur/auth/logout' : '/org/auth/logout', { method: 'POST' });
        clearCredentials();
        const loginUrl = carrying(isFounder ? ePath('/login') : loginHref());
        window.location.href = withParam(loginUrl, 'error', error);
      },

      /** An API refused the session with `account_not_active`: re-read it, so
       * the guards see the account's real state and route it to onboarding.
       * A session already known to be inactive is left to those guards. */
      handleAccountNotActive: async (): Promise<void> => {
        const { user, refreshAuth } = get();
        if (isAccountInactive(user)) return;
        await refreshAuth();
      },

      redirectToDashboard: (_role: UserRole, fallbackPath?: string) => {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTo = urlParams.get('redirect');
        const safeRedirectTo = redirectTo ? resolveSameOriginRedirect(redirectTo) : null;

        if (safeRedirectTo) {
          window.location.href = safeRedirectTo;
          return;
        }

        // Experts only have the committees area — send them there regardless of
        // the login form's role (their actual role is known post-login).
        if (get().user?.role === UserRole.EXPERT) {
          window.location.href = tPath('/committees');
          return;
        }

        window.location.href = fallbackPath || '/';
      },

      // Role-specific helpers
      isRole: (role: UserRole): boolean => {
        const { user } = get();
        return user?.role === role;
      },

      hasPermission: (permission: string): boolean => {
        const { permissions } = get();
        return permissions.includes(permission);
      },

    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        permissions: state.permissions,
        org_features: state.org_features,
        platforms: state.platforms,
        isAuthenticated: state.isAuthenticated,
        currentRole: state.currentRole,
      }),
    }
  )
);

// Helper functions
const OTHER_ME_ENDPOINT: Record<string, string> = {
  '/entrepreneur/me': '/org/auth/me',
  '/org/auth/me': '/entrepreneur/me',
};

/** Staff and entrepreneur sessions are different cookies, so a 401 from the
 * `/me` the route asks for means "no session of THIS kind", not "no session".
 * Asked only after that one refuses, so a user holding both sessions is still
 * identified by the route they are on. Returns null when there is none. */
async function fetchOtherSession(
  callApi: typeof import('@/hooks/useFetch').callApi,
  meEndpoint: string,
): Promise<UserWithPermissions | null> {
  try {
    return await callApi<UserWithPermissions>(OTHER_ME_ENDPOINT[meEndpoint]);
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) return null;
    throw error;
  }
}

function isProtectedRoute(path: string): boolean {
  const prefixes = [...PLATFORMS.map(p => `/${p}`), '/org', '/superadmin', '/profile', '/me', '/users'];
  return prefixes.some(p => path.startsWith(p))
    || isEntrepreneurPath(path)
    || hostPlatform !== null;
}

// Convenience hooks for specific roles
export const useAdminAuth = () => {
  const store = useAuthStore();
  const role = store.user?.role as UserRole | undefined;
  const isStaff = isStaffRole(role);
  const adminUser = isStaff ? store.user as StaffUser : null;
  const isSuperAdmin = role === UserRole.SUPERADMIN;
  const isOrgAdmin = role === UserRole.ORG_ADMIN;

  return {
    ...store,
    isAdmin: isStaff,
    isSuperAdmin,
    isOrgAdmin,
    adminUser,
    isActiveAdmin: adminUser?.admin_state === AdminState.ACTIVE,
    isDisabledAdmin: adminUser?.admin_state === AdminState.DISABLED,
    isPendingSetup: adminUser?.admin_state === AdminState.PENDING_SETUP,
  };
};

export const useEntrepreneurAuth = () => {
  const store = useAuthStore();
  const entrepreneurUser = store.user?.role === UserRole.ENTREPRENEUR ? store.user as EntrepreneurUser : null;
  
  return {
    ...store,
    isEntrepreneur: store.isRole(UserRole.ENTREPRENEUR),
    entrepreneurUser,
    // Entrepreneur-specific state helpers
    isPendingLinkedInParse: entrepreneurUser?.entrepreneur_state === EntrepreneurState.PENDING_LINKEDIN_PARSE,
    isPendingOnboarding: entrepreneurUser?.entrepreneur_state === EntrepreneurState.PENDING_ONBOARDING,
    isPendingSummary: entrepreneurUser?.entrepreneur_state === EntrepreneurState.PENDING_SUMMARY,
    isPendingPassedPrescore: entrepreneurUser?.entrepreneur_state === EntrepreneurState.PENDING_PASSED_PRESCORE,
    isPendingFailedPrescore: entrepreneurUser?.entrepreneur_state === EntrepreneurState.PENDING_FAILED_PRESCORE,
    isPendingWithNotes: entrepreneurUser?.entrepreneur_state === EntrepreneurState.PENDING_WITH_NOTES,
    isPending: entrepreneurUser?.entrepreneur_state && [
      EntrepreneurState.PENDING_LINKEDIN_PARSE,
      EntrepreneurState.PENDING_ONBOARDING,
      EntrepreneurState.PENDING_SUMMARY,
      EntrepreneurState.PENDING_PASSED_PRESCORE,
      EntrepreneurState.PENDING_FAILED_PRESCORE,
      EntrepreneurState.PENDING_WITH_NOTES
    ].includes(entrepreneurUser.entrepreneur_state),
    isApproved: entrepreneurUser?.entrepreneur_state === EntrepreneurState.APPROVED,
    isRejected: entrepreneurUser?.entrepreneur_state === EntrepreneurState.REJECTED || 
                 entrepreneurUser?.entrepreneur_state === EntrepreneurState.REJECTED_FAILED_PRESCORE,
  };
};

// Org feature access hook — read org_features from auth store
export const useOrgFeatures = () => {
  const org_features = useAuthStore((state) => state.org_features);
  return {
    org_features,
    hasFeature: (feature: string) => org_features.includes(feature),
    hasTerminus: org_features.includes('terminus'),
    hasFoundersHub: org_features.includes('foundershub'),
    hasGaia: org_features.includes('gaia'),
  };
};

// The caller's own entitled platforms (backend-computed, `/me`'s `platforms`
// — §8.1's "(b) las filas user_platform_access del que mira"). Distinct from
// `useOrgFeatures`, which is what the ORGANIZATION is entitled to; this is
// what THIS user may act on within it — the org panel's platform-section
// selector filters by the intersection of both.
export const usePlatforms = () => useAuthStore((state) => state.platforms);

// The org panel's platform-section selector, and every invite form that
// offers a platform choice, need the SAME intersection — the org's features
// AND this viewer's own access rows. Two independent copies of this filter
// shipped in the same commit and were caught only by review; one shared hook
// is what keeps a third from joining them.
export const useEntitledPlatforms = (): StaffPlatform[] => {
  const { org_features } = useOrgFeatures();
  const platforms = usePlatforms();
  return PLATFORMS.filter(
    (p) => org_features.includes(p) && platforms.includes(p)
  );
};

// Generic auth hook
export const useAuthState = () => useAuthStore();
